/**
 * State the HUD needs that the Milestone 1 server does not send yet.
 *
 * These types come from `doc/grind/design/` and the components in `src/ui/`
 * implement them in full. Nothing here is invented at runtime: every field is
 * empty or null until a future milestone adds the protocol message that fills
 * it, and each component renders a designed empty state in the meantime.
 *
 * Stamina used to be the one exception — a flat 100/100 that rendered as though
 * it were live. The server simulates it now, so `StaminaState` below is derived
 * from the CHARACTER frame and only its dormant-before-first-frame case is local.
 *
 * Items and the inventory used to live here as empty placeholders. They are now
 * genuinely server-backed — see the INVENTORY frame — so their types moved to
 * net/protocol.ts where the rest of the wire format lives.
 */

/**
 * What the rebirth modal shows beyond "you died", per
 * `doc/grind/design/CORE-LOOP.md`. Null until the server computes Fame on
 * death; the modal renders without the ledger in that case.
 */
export interface RebirthSummary {
  level: number;
  fame: number;
  rebirthPoints: number;
  bossKills: number;
  pvpKills: number;
}

/**
 * The seasonal wipe countdown, per `doc/grind/design/ENDGAME-BOSSES.md`.
 * Null until the server broadcasts the apocalypse; the banner is not rendered.
 */
export interface ApocalypseState {
  /** The player who hit level 99 and triggered the season's end. */
  triggeredBy: string;
  /** Epoch milliseconds at which the world wipes. */
  endsAt: number;
}

/**
 * Stamina, per `doc/grind/design/CLASSES-ATTRIBUTES.md`.
 *
 * This used to be a flat 100/100 placeholder, from back when the server had no
 * stamina at all. It does now — skills spend it and it recovers after
 * `StaminaRegenDelay` — so the store derives this from the `CHARACTER` frame's
 * pools and there is nothing invented left in it.
 *
 * What remains is the gap *before* that frame arrives. `live` is false then, and
 * the bar renders as dormant rather than guessing at a number: an empty bar that
 * says so is honest, a full one is a lie the player cannot see through.
 */
export interface StaminaState {
  current: number;
  max: number;
  /** True once a CHARACTER frame has arrived and these numbers are the server's. */
  live: boolean;
}

/** What the bar shows before the first CHARACTER frame. */
export const DORMANT_STAMINA: StaminaState = {
  current: 0,
  max: 0,
  live: false,
};

/*
 * The action bar's five slots used to be described here: a hand-written list
 * named Heavy Strike, Shadow Step and a SPACE-keyed Dodge Roll, from before the
 * server had skills at all.
 *
 * It outlived its usefulness badly. The server has five slots keyed 1–5; that
 * list had three keyed 1–3, one on SPACE and one on LMB, so two server slots had
 * no button and one button answered to no key — pressing it did nothing, which is
 * exactly how it was reported. The bar is generated from `SKILL_SLOT_COUNT` in
 * `ui/actionbar.ts` now, and each slot shows whatever the player actually put in
 * it.
 */
